/**
 * Shared presentational kit for the bulk download screens (labels / invoices / POD).
 * Gives every download page the same modern look:
 *   - DownloadHero  : gradient header with an icon badge + optional info pill chips
 *   - SectionHead   : icon-accented section header with an optional numbered step badge
 *   - FormField     : a field wrapper that renders the LABEL ABOVE the control (keeps
 *                     every input perfectly aligned regardless of control type)
 *   - ToggleField   : a modern switch/pill replacing the old MUI checkbox
 *   - modernInputsSx: drop-in sx for the form wrapper to restyle all MUI inputs
 * Purely presentational — no submit logic lives here.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import { useFormContext, Controller } from 'react-hook-form'
import { pageHeroSx, heroTitleSx, heroSubSx } from '../../../styles/patterns'
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography'
import { NAVY, SUBTLE, MUTED, BORDER, PRIMARY_BLUE, ERROR, WHITE } from '../../../styles/foundation/colors'

// ── Gradient page header with an icon badge + optional pill chips ───────────────
export function DownloadHero({ icon, title, subtitle, chips = [] }) {
    return (
        <Box sx={pageHeroSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
                {icon && (
                    <Box sx={{
                        width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
                        bgcolor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
                        display: 'grid', placeItems: 'center', color: '#fff',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                    }}>
                        {React.cloneElement(icon, { sx: { fontSize: 28, color: '#fff' } })}
                    </Box>
                )}
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={heroTitleSx}>{title}</Typography>
                    {subtitle && <Typography sx={heroSubSx}>{subtitle}</Typography>}
                </Box>
            </Box>

            {chips.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.75, position: 'relative', zIndex: 1 }}>
                    {chips.map((c, i) => (
                        <Box key={i} sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            bgcolor: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.16)',
                            borderRadius: '999px', px: 1.25, py: 0.5,
                        }}>
                            {c.icon && React.cloneElement(c.icon, { sx: { fontSize: 15, color: 'rgba(255,255,255,0.85)' } })}
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: 'rgba(255,255,255,0.88)' }}>
                                {c.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

// ── Icon-accented section header with an optional numbered step badge ───────────
export function SectionHead({ icon, title, desc, accent = PRIMARY_BLUE, step }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: '10px', flexShrink: 0, position: 'relative',
                bgcolor: accent + '14', color: accent, display: 'grid', placeItems: 'center',
            }}>
                {React.cloneElement(icon, { sx: { fontSize: 20, color: accent } })}
                {step != null && (
                    <Box sx={{
                        position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%',
                        bgcolor: accent, color: '#fff', fontSize: 10, fontWeight: 700, fontFamily: FONT,
                        display: 'grid', placeItems: 'center',
                    }}>{step}</Box>
                )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY, lineHeight: 1.2 }}>
                    {title}
                </Typography>
                {desc && (
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25 }}>
                        {desc}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

// ── Field wrapper: label ABOVE the control (uniform alignment across input types) ──
export function FormField({ label, required, hint, children }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Typography component="label" sx={{
                fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.semibold, color: SUBTLE,
                textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.4,
            }}>
                {label}{required && <Box component="span" sx={{ color: ERROR, ml: 0.25 }}>*</Box>}
            </Typography>
            {children}
            {hint && (
                <Typography sx={{ fontFamily: FONT, fontSize: '10.5px', color: MUTED, lineHeight: 1.3 }}>
                    {hint}
                </Typography>
            )}
        </Box>
    )
}

// ── Modern toggle (replaces old MUI checkbox): a clickable card with a pill switch ──
export function ToggleField({ name, label, icon }) {
    const { control } = useFormContext()
    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                const on = !!field.value
                return (
                    <Box
                        onClick={() => field.onChange(!on)}
                        role="switch"
                        aria-checked={on}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none',
                            px: 1.5, py: 1, borderRadius: '10px', minWidth: 190, flex: '1 1 auto',
                            border: `1px solid ${on ? PRIMARY_BLUE : BORDER}`,
                            bgcolor: on ? PRIMARY_BLUE + '0d' : WHITE,
                            transition: 'all 0.15s ease',
                            '&:hover': { borderColor: PRIMARY_BLUE, boxShadow: `0 2px 8px ${PRIMARY_BLUE}1f` },
                        }}
                    >
                        {icon && React.cloneElement(icon, { sx: { fontSize: 18, color: on ? PRIMARY_BLUE : MUTED } })}
                        <Typography sx={{
                            flex: 1, fontFamily: FONT, fontSize: '12.5px', fontWeight: WEIGHT.medium,
                            color: on ? NAVY : SUBTLE,
                        }}>
                            {label}
                        </Typography>
                        {/* pill switch (purely visual — the whole card toggles) */}
                        <Box sx={{
                            width: 34, height: 20, borderRadius: '999px', flexShrink: 0, position: 'relative',
                            bgcolor: on ? PRIMARY_BLUE : '#cfd8e3', transition: 'background 0.15s ease',
                        }}>
                            <Box sx={{
                                position: 'absolute', top: 2, left: on ? 16 : 2, width: 16, height: 16,
                                borderRadius: '50%', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.28)',
                                transition: 'left 0.15s ease',
                            }} />
                        </Box>
                    </Box>
                )
            }}
        />
    )
}

// ── Drop-in sx for the form wrapper: modernises every MUI input inside it ───────
// Labels are rendered above via FormField, so inputs carry no floating label — this
// just rounds the corners, gives a consistent height, and adds a clean focus ring.
export const modernInputsSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: WHITE,
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '& fieldset': { borderColor: BORDER },
        '&:hover fieldset': { borderColor: PRIMARY_BLUE + '88' },
        '&.Mui-focused fieldset': { borderColor: PRIMARY_BLUE, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${PRIMARY_BLUE}1f` },
    },
    '& .MuiOutlinedInput-input': { fontSize: 13, padding: '10.5px 12px' },
    '& .MuiAutocomplete-inputRoot': { paddingTop: '3px', paddingBottom: '3px' },
    // no floating labels in this layout — hide any that slip through so nothing overlaps the border
    '& .MuiInputLabel-root': { display: 'none' },
    '& .MuiOutlinedInput-notchedOutline legend': { width: 0 },
}

// ── Call-to-action buttons ──────────────────────────────────────────────────────
export const ctaBtnSx = {
    fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, textTransform: 'none',
    color: WHITE, px: 2.75, py: 1, borderRadius: '10px', whiteSpace: 'nowrap',
    background: 'linear-gradient(135deg, #3783e7 0%, #2f6fd0 100%)',
    boxShadow: '0 6px 16px rgba(55,131,231,0.32)',
    '&:hover': { background: 'linear-gradient(135deg, #3783e7 0%, #2f6fd0 100%)', filter: 'brightness(1.06)' },
}

export const ghostBtnSx = {
    fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, textTransform: 'none',
    color: SUBTLE, px: 2.25, py: 1, borderRadius: '10px', whiteSpace: 'nowrap',
    border: `1px solid ${BORDER}`,
    '&:hover': { borderColor: PRIMARY_BLUE, bgcolor: PRIMARY_BLUE + '08', color: NAVY },
}
